import { IdentityUserAri } from '@atlassian/ari';
import type { PreloadedQuery } from 'react-relay';

import { AggListQuery, type AggListQueryType } from '@townsquare/agg-list/query';
import { createRelayResource } from '@townsquare/relay-utils';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import type { RouteResource } from '@townsquare/router/routing-types';
import type { HomeResourceContext } from '@townsquare/router/types';
import { fg } from '@townsquare/stat-sig/gate';
import { type GateNames } from '@townsquare/stat-sig/gate-keys';

import { ALL_APPS, type App, APP_QUERY_PARAM, parseAppParam } from '../types';

type EdgeGroup = {
	app: App;
	edges: string[];
	requiredGate?: GateNames;
};

const EDGE_GROUPS: EdgeGroup[] = [
	{
		app: 'confluence',
		edges: [
			'user_favorited_confluence_page',
			'user_favorited_confluence_blogpost',
			'user_favorited_confluence_whiteboard',
			'user_favorited_confluence_database',
		],
	},
	{ app: 'projects', edges: ['user_favorited_townsquare_project'] },
	{ app: 'goals', edges: ['user_favorited_townsquare_goal'] },
	{
		app: 'teams',
		edges: ['user_favorited_team'],
	},
];

export const createCypherQuery = (accountId: string, specifiedApps: App[]) => {
	const apps = specifiedApps.length === 0 ? ALL_APPS : specifiedApps;

	const edges = EDGE_GROUPS.flatMap((edgeGroup: EdgeGroup) => {
		if (!apps.includes(edgeGroup.app)) {
			// If this app is not requested, don't include its edges.
			return [];
		} else if (edgeGroup.requiredGate && !fg(edgeGroup.requiredGate)) {
			// If this app requires a feature gate and the gate is off, don't include its edges.
			return [];
		} else {
			return edgeGroup.edges;
		}
	});

	const userAri = IdentityUserAri.create({ userId: accountId }).toString();

	const edgeList = edges.join('|');

	return `MATCH (user: IdentityUser {ari: '${userAri}'})-[rel:${edgeList}]->(entity) RETURN entity, rel.createdAt ORDER BY rel.createdAt DESC`;
};

export const starredFeedResource: RouteResource<PreloadedQuery<AggListQueryType>> =
	createRelayResource<AggListQueryType>({
		getQuery: (routerContext, resourceContext) => {
			const context = resourceContext as HomeResourceContext;
			const accountId = context.user.accountId;
			const apps = parseAppParam(routerContext.query[APP_QUERY_PARAM]);
			const isUGSHeadersMigrationEnabled = fg('townsquare_ugs_headers_migration');

			const cypherQuery = createCypherQuery(accountId, apps);

			return {
				environment: getAggRelayEnvironment(),
				query: AggListQuery,
				variables: {
					cypherQuery,
					queryContext: isUGSHeadersMigrationEnabled
						? `ari:cloud:platform::site/${context?.collabContext?.rovoWorkspace}`
						: `ari:cloud:platform::org/${context?.orgId}`,
					isEnabled: !!context?.orgId,
					first: fg('townsquare_starred_workarounds') ? 250 : 100,
				},
				options: {
					fetchPolicy: 'store-and-network',
				},
			};
		},
	});
